import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import { listMyCreatorSubmissions } from "../../../services/creatorApi";
import { TrainingLoading } from "../../academy/AcademyShared";
import {
  CreatorLayout,
  CREATOR_CRUMBS,
  CreatorProgress,
  CreatorStatusChip,
  CreatorDecisionBanner,
} from "./CreatorShared";
import { getSubmissionProgress, isV2Brief } from "./creatorModels";

function workCtaLabel(item) {
  if (item.status === "retake") {
    return isV2Brief(item) ? "Fix performances" : "Fix clips";
  }
  if (item.status === "in_progress") return item.uploads_closed ? "View status" : "Continue";
  if (item.status === "submitted") return "View status";
  if (item.status === "rejected") return "View decision";
  if (item.status === "approved") return "View payments";
  return "Open";
}

export default function CreatorMyWork() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/creator/work";
      return;
    }
    setLoading(true);
    listMyCreatorSubmissions()
      .then((res) => setItems(res.submissions || []))
      .catch((e) => setError(e.message || "Could not load your work"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <CreatorLayout breadcrumbs={CREATOR_CRUMBS.work} backTo="/me/creator">
        <TrainingLoading label="Loading your work..." />
      </CreatorLayout>
    );
  }

  const continueItem = items.find(
    (i) => i.status === "retake" || (i.status === "in_progress" && !i.uploads_closed)
  );
  const continueProgress = continueItem ? getSubmissionProgress(continueItem) : null;

  return (
    <CreatorLayout breadcrumbs={CREATOR_CRUMBS.work} backTo="/me/creator">
      <section className="creator-hero">
        <p className="training-kicker">Your work</p>
        <h2 className="creator-hero__title">My scripts</h2>
        <p className="creator-hero__lede">
          Fix retakes first, then finish uploads, then wait for review.
        </p>
      </section>

      {error ? <p className="creator-error">{error}</p> : null}

      {continueItem ? (
        <Link
          className="training-continue-card creator-continue"
          to={`/me/creator/work/${continueItem.id}`}
        >
          <span className="training-continue-label">
            {continueItem.status === "retake" ? "Fix retake" : "Continue"}
          </span>
          <strong className="training-continue-title">
            {continueItem.script.code} · {continueItem.script.title}
          </strong>
          <span className="training-continue-meta">
            {continueProgress.label} · {continueItem.payment_label}
          </span>
          <progress
            className="creator-progress creator-progress--on-dark"
            max={continueProgress.needed || 1}
            value={continueProgress.ready}
            aria-label={`${continueItem.script.code}: ${continueProgress.label} ready`}
          />
        </Link>
      ) : null}

      {!items.length ? (
        <div className="training-empty">
          <p>No scripts started yet.</p>
          <Link className="training-btn-primary" to="/me/creator">
            Browse scripts
          </Link>
        </div>
      ) : (
        <div className="creator-work-list">
          {items.map((item) => (
            <article key={item.id} className="training-card creator-work-card">
              <div className="creator-work-card__top">
                <CreatorStatusChip status={item.status} />
                <span className="creator-script-card__pay">{item.payment_label}</span>
              </div>
              <h3 className="creator-work-card__title">
                {item.script.code} · {item.script.title}
              </h3>
              <CreatorProgress submission={item} prefix={item.script.code} />
              {item.uploads_closed ? (
                <div className="creator-brief-callout creator-brief-callout--avoid" role="status">
                  <strong>This script is no longer available</strong>
                  <p>
                    {item.script.unavailable_message ||
                      "The deadline has passed, so new uploads are closed."}
                  </p>
                </div>
              ) : null}
              {["retake", "rejected", "approved"].includes(item.status) ? (
                <CreatorDecisionBanner status={item.status} note={item.admin_note} />
              ) : null}
              <Link
                className="training-btn-primary"
                to={
                  item.status === "approved"
                    ? "/me/creator/payments"
                    : `/me/creator/work/${item.id}`
                }
              >
                {workCtaLabel(item)}
              </Link>
            </article>
          ))}
        </div>
      )}
    </CreatorLayout>
  );
}
